import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext'; 
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabase'; 
import '../styles/Profile.css'; 
import InviteUsersModal from '../components/InviteUsersModal';

function Profile() {
  const navigate = useNavigate();
  const { authUserId, selectedWorkspaceId } = useWorkspace(); 
  const { language } = useLanguage();
  const [userProfile, setUserProfile] = useState({
    firstname: '',
    lastname: '',
    phone_number: '',
    linkedin_url: '',
    system_role: '',
    email: '',
  });
  const [authEmail, setAuthEmail] = useState('');
  const [companyProfile, setCompanyProfile] = useState({
    company_name: '',
    website_url: '',
    company_address: '',
    client_portal_welcome_message: '',
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedSection, setSelectedSection] = useState('yourProfile'); 
  const [isCompanyProfileVisible, setIsCompanyProfileVisible] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const [selectedTab, setSelectedTab] = useState('all');
  const [openDropdownId, setOpenDropdownId] = useState(null);

  const roles = [
    { value: 'ws_admin', label: 'Admin' },
    { value: 'ws_contributor', label: 'Contributor' },
    { value: 'ws_reporter', label: 'Reporter' }
  ];

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError) throw authError;
        
        if (user) {
          setAuthEmail(user.email);
          const { data, error } = await supabase
            .from('user_profiles')
            .select('firstname, lastname, phone_number, linkedin_url, system_role')
            .eq('id', authUserId)
            .single();

          if (error) throw error;
          setUserProfile(data);

          // Check for both org_ prefix and ws_admin role
          if (data.system_role.startsWith('org_')) {
            setIsCompanyProfileVisible(true);
          } else {
            // If not org_, check for ws_admin role
            const { data: workspaceRole } = await supabase
              .from('user_workspace_roles')
              .select('workspace_role')
              .eq('user_id', authUserId)
              .eq('workspace_id', selectedWorkspaceId)
              .eq('workspace_role', 'ws_admin')
              .single();

            setIsCompanyProfileVisible(!!workspaceRole);
          }
        }
      } catch (error) {
        setErrorMessage('Error fetching profile data');
        console.error(error);
      }
    };

    const fetchCompanyProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('clients')
          .select('name, website_url, company_address, client_portal_welcome_message')
          .eq('id', selectedWorkspaceId) 
          .single();
        
        if (error) throw error;
        setCompanyProfile({
          company_name: data.name,
          website_url: data.website_url,
          company_address: data.company_address,
          client_portal_welcome_message: data.client_portal_welcome_message,
        });

      } catch (error) {
        setErrorMessage('Error fetching company profile data');
        console.error(error);
      }
    };

    const fetchWorkspaceUsers = async () => {
      try {
        const { data: users, error } = await supabase
          .from('user_workspace_roles')
          .select('user_id, workspace_role, has_sensitive_access')
          .eq('workspace_id', selectedWorkspaceId);

        if (error) throw error;

        // Get fullnames from user_profiles
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, fullname');

        // Match and combine the data
        const usersWithFullnames = users.map(user => ({
          ...user,
          fullname: profiles.find(p => p.id === user.user_id)?.fullname || user.user_id
        }));

        setWorkspaceUsers(usersWithFullnames);
        console.log('Fetched users:', usersWithFullnames);
      } catch (error) {
        console.error('Error fetching workspace users:', error);
      }
    };

    if (authUserId) {
      fetchUserProfile();
    }

    if (selectedWorkspaceId) {
      fetchCompanyProfile();
      fetchWorkspaceUsers();
    }
  }, [authUserId, selectedWorkspaceId]);

  useEffect(() => {
    const handleProfileSectionChange = (event) => {
      handleSectionClick(event.detail);
    };

    window.addEventListener('profileSectionChange', handleProfileSectionChange);

    return () => {
      window.removeEventListener('profileSectionChange', handleProfileSectionChange);
    };
  }, []);

  const handleUserProfileChange = (e) => {
    const { name, value } = e.target;
    setUserProfile((prevProfile) => ({
      ...prevProfile,
      [name]: value,
    }));
  };

  const handleCompanyProfileChange = (e) => {
    const { name, value } = e.target;
    setCompanyProfile((prevProfile) => ({
      ...prevProfile,
      [name]: value,
    }));
  };

  const handleSaveUserProfile = async (field, value) => {
    try {
      let updateData = { [field]: value };
      
      // If firstname or lastname is updated, also update fullname
      if (field === 'firstname' || field === 'lastname') {
        const newFullname = field === 'firstname' 
          ? `${value} ${userProfile.lastname}`
          : `${userProfile.firstname} ${value}`;
        updateData.fullname = newFullname;
      }

      const { error } = await supabase
        .from('user_profiles')
        .update(updateData)
        .eq('id', authUserId);

      if (error) throw error;
      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Error updating profile');
      console.error(error);
    }
  };

  const handleSaveCompanyProfile = async (field, value) => {
    try {
      const updateData = {};
      if (field === 'company_name') {
        updateData.name = value;
      } else {
        updateData[field] = value;
      }

      const { error } = await supabase
        .from('clients')
        .update(updateData)
        .eq('id', selectedWorkspaceId);

      if (error) throw error;
      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Error updating company profile');
      console.error(error);
    }
  };

  const handleSectionClick = (section) => {
    setSelectedSection(section);
  };

  const handleBlur = (e, type) => {
    const { name, value } = e.target;
    if (type === 'user') {
      handleSaveUserProfile(name, value);
    } else if (type === 'company') {
      handleSaveCompanyProfile(name, value);
    }
  };

  const handlePasswordReset = () => {
    navigate('/reset');
  };

  const filteredUsers = () => {
    switch (selectedTab) {
      case 'admin':
        return workspaceUsers.filter(user => user.workspace_role === 'ws_admin');
      case 'contributor':
        return workspaceUsers.filter(user => user.workspace_role === 'ws_contributor');
      case 'reporter':
        return workspaceUsers.filter(user => user.workspace_role === 'ws_reporter');
      case 'sensitive':
        return workspaceUsers.filter(user => user.has_sensitive_access);
      default:
        return workspaceUsers;
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const { error } = await supabase
        .from('user_workspace_roles')
        .update({ workspace_role: newRole })
        .eq('user_id', userId)
        .eq('workspace_id', selectedWorkspaceId);

      if (error) throw error;

      // Update local state
      setWorkspaceUsers(prevUsers => 
        prevUsers.map(user => 
          user.user_id === userId 
            ? { ...user, workspace_role: newRole }
            : user
        )
      );
    } catch (error) {
      console.error('Error changing role:', error);
      setErrorMessage('Error changing user role');
    }
  };

  const handleSensitiveAccessToggle = async (userId, currentAccess) => {
    try {
      const newAccess = !currentAccess;
      const { error } = await supabase
        .from('user_workspace_roles')
        .update({ has_sensitive_access: newAccess })
        .eq('user_id', userId)
        .eq('workspace_id', selectedWorkspaceId);

      if (error) throw error;

      // Update local state
      setWorkspaceUsers(prevUsers => 
        prevUsers.map(user => 
          user.user_id === userId 
            ? { ...user, has_sensitive_access: newAccess }
            : user
        )
      );
    } catch (error) {
      console.error('Error toggling sensitive access:', error);
      setErrorMessage('Error toggling sensitive access');
    }
  };

  return (
    <div className="profile-container">
      <h1 
        className={`profile-title ${selectedSection === 'yourProfile' ? 'active' : ''}`}
        onClick={() => handleSectionClick('yourProfile')}
      >
        {language === 'en' ? 'Your Profile' : 'Ihr Profil'}
        {isCompanyProfileVisible && (
          <>
            <span
              className={`profile-subtitle ${selectedSection === 'companyProfile' ? 'active' : ''}`}
              onClick={(e) => { 
                e.stopPropagation(); 
                handleSectionClick('companyProfile');
              }}
            >
              {language === 'en' ? 'Company Profile' : 'Unternehmensprofil'}
            </span>
            <span
              className={`profile-subtitle ${selectedSection === 'team' ? 'active' : ''}`}
              onClick={(e) => { 
                e.stopPropagation(); 
                handleSectionClick('team');
              }}
            >
              {language === 'en' ? 'Team' : 'Team'}
            </span>
          </>
        )}
      </h1>
      {errorMessage && <p className="error">{errorMessage}</p>}

      <hr className="profile-divider" />

      <div className="profile-sections">
        {selectedSection === 'team' ? (
          <div className="team-section">
            <h2>{language === 'en' ? 'Team' : 'Team'}</h2>
            <p className="section-description">
              <strong>{language === 'en' ? 'Invite new users and manage members' : 'Laden Sie neue Benutzer ein und verwalten Sie Mitglieder'}</strong>
            </p>
            <button 
              className="invite-button"
              onClick={() => setIsInviteModalOpen(true)}
            >
              <strong>{language === 'en' ? 'Invite additional users' : 'Zusätzliche Benutzer einladen'}</strong>
            </button>
            <InviteUsersModal 
              isOpen={isInviteModalOpen} 
              onClose={() => setIsInviteModalOpen(false)}
              selectedWorkspaceId={selectedWorkspaceId}
            />
            <div className="workspace-users-table">
              <div className="workspace-tabs">
                <button 
                  className={`tab ${selectedTab === 'all' ? 'active' : ''}`}
                  onClick={() => setSelectedTab('all')}
                >
                  {language === 'en' ? 'All' : 'Alle'} ({workspaceUsers.length})
                </button>
                <button 
                  className={`tab ${selectedTab === 'admin' ? 'active' : ''}`}
                  onClick={() => setSelectedTab('admin')}
                >
                  {language === 'en' ? 'Admin' : 'Administrator'} ({workspaceUsers.filter(u => u.workspace_role === 'ws_admin').length})
                </button>
                <button 
                  className={`tab ${selectedTab === 'contributor' ? 'active' : ''}`}
                  onClick={() => setSelectedTab('contributor')}
                >
                  {language === 'en' ? 'Contributor' : 'Mitwirkender'} ({workspaceUsers.filter(u => u.workspace_role === 'ws_contributor').length})
                </button>
                <button 
                  className={`tab ${selectedTab === 'reporter' ? 'active' : ''}`}
                  onClick={() => setSelectedTab('reporter')}
                >
                  {language === 'en' ? 'Reporter' : 'Berichterstatter'} ({workspaceUsers.filter(u => u.workspace_role === 'ws_reporter').length})
                </button>
                <button 
                  className={`tab ${selectedTab === 'sensitive' ? 'active' : ''}`}
                  onClick={() => setSelectedTab('sensitive')}
                >
                  {language === 'en' ? 'Sensitive Access' : 'Sensibler Zugriff'} ({workspaceUsers.filter(u => u.has_sensitive_access).length})
                </button>
              </div>
              <table className="users-table">
                <thead>
                  <tr>
                    <th>{language === 'en' ? 'Name' : 'Name'}</th>
                    <th>{language === 'en' ? 'Roles' : 'Rollen'}</th>
                    <th>{language === 'en' ? 'Sensitive Access' : 'Sensibler Zugriff'}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers().length > 0 ? (
                    filteredUsers().map((user, index) => (
                      <tr key={index}>
                        <td>{user.fullname}</td>
                        <td>
                          <div className="role-selector-container" style={{ position: 'relative' }}>
                            <button 
                              className="role-selector"
                              onClick={() => setOpenDropdownId(openDropdownId === user.user_id ? null : user.user_id)}
                            >
                              {roles.find(r => r.value === user.workspace_role)?.label || user.workspace_role}
                            </button>
                            {openDropdownId === user.user_id && (
                              <div className="role-dropdown-menu">
                                {roles.map(role => (
                                  <div
                                    key={role.value}
                                    className="role-dropdown-item"
                                    onClick={() => {
                                      handleRoleChange(user.user_id, role.value);
                                      setOpenDropdownId(null);
                                    }}
                                  >
                                    {role.label}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            checked={user.has_sensitive_access || false}
                            onChange={() => handleSensitiveAccessToggle(user.user_id, user.has_sensitive_access)}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3">{language === 'en' ? 'No users found for workspace' : 'Keine Benutzer für den Arbeitsbereich gefunden'}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="system-roles">
              <h3>{language === 'en' ? 'System Roles' : 'Systemrollen'}</h3>
              <p className="roles-description">
                {language === 'en' 
                  ? 'Our client portal uses a role-based access system to ensure secure and efficient collaboration while maintaining data privacy.'
                  : 'Unser Kundenportal verwendet ein rollenbasiertes Zugriffssystem, um eine sichere und effiziente Zusammenarbeit bei gleichzeitiger Wahrung der Datenschutzbestimmungen zu gewährleisten.'
                }
              </p>
              
              <table className="roles-table">
                <thead>
                  <tr>
                    <th>{language === 'en' ? 'Role' : 'Rolle'}</th>
                    <th>{language === 'en' ? 'Description' : 'Beschreibung'}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>{language === 'en' ? 'Admin' : 'Administrator'}</strong></td>
                    <td>
                      <ul>
                        <strong>
                        <li>{language === 'en' ? 'Can access and edit everything (incl. settings)' : 'Kann auf alles zugreifen und alles bearbeiten (einschließlich Einstellungen)'}</li>
                        <li>{language === 'en' ? 'Typically assigned to application project leads and senior management members' : 'Wird typischerweise Projektleitern und Mitgliedern des Senior Managements zugewiesen'}</li>
                        </strong>
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>{language === 'en' ? 'Contributor' : 'Mitwirkender'}</strong></td>
                    <td>
                      <ul>
                        <strong>
                        <li>{language === 'en' ? 'Can access and edit applications and documents' : 'Kann auf Anträge und Dokumente zugreifen und diese bearbeiten'}</li>
                        <li>{language === 'en' ? 'Can edit their own employee profile' : 'Kann sein eigenes Mitarbeiterprofil bearbeiten'}</li>
                        <li>{language === 'en' ? 'Can be given selective access to sensitive materials (e.g. employee data, sensitive files)' : 'Kann selektiven Zugriff auf sensible Materialien erhalten (z.B. Mitarbeiterdaten, sensible Dateien)'}</li>
                        <li>{language === 'en' ? 'Typically for team members actively involved in grant applications' : 'Typischerweise für Teammitglieder, die aktiv an Zuschussanträgen beteiligt sind'}</li>
                        </strong>
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>{language === 'en' ? 'Reporter' : 'Berichterstatter'}</strong></td>
                    <td>
                      <ul>
                        <strong>
                        <li>{language === 'en' ? 'Can submit and edit their own timesheets' : 'Kann eigene Zeiterfassungen einreichen und bearbeiten'}</li>
                        <li>{language === 'en' ? 'Can create and edit project reports' : 'Kann Projektberichte erstellen und bearbeiten'}</li>
                        <li>{language === 'en' ? 'Data access limited to their own data entries' : 'Datenzugriff auf eigene Dateneinträge beschränkt'}</li>
                        <li>{language === 'en' ? 'Perfect for team members who need to document their work for grant reporting' : 'Ideal für Teammitglieder, die ihre Arbeit für Zuschussberichte dokumentieren müssen'}</li>
                        </strong>
                      </ul>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="sensitive-access-note">
                <p>
                  <span className="lock-icon">🔒</span> <strong>{language === 'en' ? 'has sensitive access' : 'hat sensiblen Zugriff'}</strong> {language === 'en' 
                    ? 'indicates that a user has been granted access to sensitive materials within their workspace. This permission can be managed individually by workspace admins and is typically given to team members who need access to confidential information like employee data or sensitive financial documents.'
                    : 'bedeutet, dass einem Benutzer Zugriff auf sensible Materialien in seinem Arbeitsbereich gewährt wurde. Diese Berechtigung kann von Arbeitsbereichsadministratoren individuell verwaltet werden und wird typischerweise Teammitgliedern gewährt, die Zugriff auf vertrauliche Informationen wie Mitarbeiterdaten oder sensible Finanzdokumente benötigen.'
                  }
                </p>
              </div>
            </div>
          </div>
        ) : selectedSection === 'yourProfile' ? (
          <div>
            <h2>{language === 'en' ? 'Profile' : 'Profil'}</h2>
            <p className="section-description"><strong>{language === 'en' ? 'Manage your profile' : 'Verwalten Sie Ihr Profil'}</strong></p>
  
            <div className="profile-form">
              <label>{language === 'en' ? 'First Name' : 'Vorname'}</label>
              <input
                type="text"
                name="firstname"
                value={userProfile.firstname}
                onChange={handleUserProfileChange}
                onBlur={(e) => handleBlur(e, 'user')}
              />
  
              <label>{language === 'en' ? 'Last Name' : 'Nachname'}</label>
              <input
                type="text"
                name="lastname"
                value={userProfile.lastname}
                onChange={handleUserProfileChange}
                onBlur={(e) => handleBlur(e, 'user')}
              />
  
              <label>{language === 'en' ? 'Phone' : 'Telefon'}</label>
              <input
                type="tel"
                name="phone_number"
                value={userProfile.phone_number}
                onChange={handleUserProfileChange}
                onBlur={(e) => handleBlur(e, 'user')}
              />
  
              <label>{language === 'en' ? 'LinkedIn' : 'LinkedIn'}</label>
              <input
                type="url"
                name="linkedin_url"
                value={userProfile.linkedin_url}
                onChange={handleUserProfileChange}
                onBlur={(e) => handleBlur(e, 'user')}
              />
            </div>
  
            <hr className="profile-divider" />
            <h2>{language === 'en' ? 'Account' : 'Konto'}</h2>
            <p className="section-description"><strong>{language === 'en' ? 'Update account details' : 'Kontodetails aktualisieren'}</strong></p>
  
            <div className="profile-form">
              <label>{language === 'en' ? 'Email' : 'E-Mail'}</label>
              <input
                type="email"
                value={authEmail}
                disabled
              />
              <label>{language === 'en' ? 'Password' : 'Passwort'}</label>
              <input
                type="text"
                value={language === 'en' ? 'Change Password' : 'Passwort ändern'}
                onClick={handlePasswordReset}
                className="password-field"
                readOnly
              />
            </div>
          </div>
        ) : (
          <div>
            <h2>{language === 'en' ? 'Company Profile' : 'Unternehmensprofil'}</h2>
            <p className="section-description"><strong>{language === 'en' ? 'Manage your company profile' : 'Verwalten Sie Ihr Unternehmensprofil'}</strong></p>
    
            <div className="profile-form">
              <label>{language === 'en' ? 'Company Name' : 'Unternehmensname'}</label>
              <input
                type="text"
                name="company_name"
                value={companyProfile.company_name}
                onChange={handleCompanyProfileChange}
                onBlur={(e) => handleBlur(e, 'company')}
              />
    
              <label>{language === 'en' ? 'Company URL' : 'Unternehmens-URL'}</label>
              <input
                type="url"
                name="website_url"
                value={companyProfile.website_url}
                onChange={handleCompanyProfileChange}
                onBlur={(e) => handleBlur(e, 'company')}
              />
    
              <label>{language === 'en' ? 'Company Address' : 'Unternehmensadresse'}</label>
              <input
                type="text"
                name="company_address"
                value={companyProfile.company_address}
                onChange={handleCompanyProfileChange}
                onBlur={(e) => handleBlur(e, 'company')}
              />
            </div>
    
            <hr className="profile-divider" />
            <h2>{language === 'en' ? 'Default Settings' : 'Standardeinstellungen'}</h2>
            <p className="section-description"><strong>{language === 'en' ? 'Update the company welcome message' : 'Aktualisieren Sie die Willkommensnachricht des Unternehmens'}</strong></p>
    
            <div className="profile-form">
              <label>{language === 'en' ? 'Company Message' : 'Unternehmensnachricht'}</label>
              <textarea
                name="client_portal_welcome_message"
                value={companyProfile.client_portal_welcome_message}
                onChange={handleCompanyProfileChange}
                onBlur={(e) => handleBlur(e, 'company')}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Profile;
